// useBusinessForm.ts

import { useState, useEffect } from "react";
import { ProfileData } from "../types/profile";

interface Phone {
  phone: string;
  isPrimary: boolean;
}

interface Email {
  email: string;
  isPrimary: boolean;
}

export interface BusinessFormData {
  _id: {
    $oid: string;
  };

  addressLine1: string;
  addressline2?: string;

  businessType: string;

  city: string;
  country: string;
  state: string;
  postalCode: string;

  dob: string;

  emails: Email[];
  phones: Phone[];

  firstName: string;
  middleName?: string;
  lastName?: string;

  arabicName: string;

  orgName: string;
  orgId: string;

  role: string | string[] | null;

  timeZone: string | number | null;

  userName: string;

  businessLocation: string;
}

export const useBusinessForm = (profileData: ProfileData | null) => {
  const [formData, setFormData] = useState<BusinessFormData>({
    _id: { $oid: "" },

    addressLine1: "",
    addressline2: "",

    businessType: "",

    city: "",
    country: "",
    state: "",
    postalCode: "",

    dob: "",

    emails: [],
    phones: [],

    firstName: "",
    middleName: "",
    lastName: "",

    arabicName: "",

    orgName: "",
    orgId: "",

    role: null,

    timeZone: null,

    userName: "",

    businessLocation: "",
  });

  useEffect(() => {
    if (profileData) {
      let formattedDob = "";

      try {
        const rawDob = typeof profileData.dob === "string" ? profileData.dob : profileData.dob?.$date;

        if (rawDob) {
          const parsedDate = new Date(rawDob);

          if (!isNaN(parsedDate.getTime())) {
            formattedDob = parsedDate.toISOString().split("T")[0];
          }
        }
      } catch (error) {
        console.error("Invalid DOB:", error);
      }

      setFormData({
        _id: profileData._id || { $oid: "" },

        addressLine1: profileData.addressLine1 || "",
        addressline2: profileData.addressline2 || "",

        businessType: profileData.businessType || "",

        city: profileData.city || "",
        country: profileData.country || "",
        state: profileData.state || "",
        postalCode: profileData.postalCode || "",

        dob: formattedDob,

        emails: profileData.emails || [],
        phones: profileData.phones || [],

        firstName: profileData.firstName || "",
        middleName: profileData.middleName || "",
        lastName: profileData.lastName || "",

        arabicName: profileData.arabicName || "",

        orgName: profileData.orgName || "",
        orgId: profileData.orgId || "",

        role: profileData.role || null,

        timeZone: profileData.timeZone || null,

        userName: profileData.userName || "",

        businessLocation: profileData.businessLocation || "",
      });
    }
  }, [profileData]);

  const isValidDate = (value: string) => {
    // Accept only YYYY-MM-DD
    const regex = /^\d{4}-\d{2}-\d{2}$/;

    if (!regex.test(value)) {
      return false;
    }

    const date = new Date(value);

    return !isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;

    // Phone handling
    if (name === "phone") {
      const numericValue = value.replace(/\D/g, "").slice(0, 8);

      setFormData((prev) => ({
        ...prev,
        phones: prev.phones?.length
          ? prev.phones.map((phone, index) =>
              index === 0
                ? {
                    ...phone,
                    phone: numericValue,
                  }
                : phone,
            )
          : [
              {
                phone: numericValue,
                isPrimary: true,
              },
            ],
      }));

      return;
    }

    // Email handling
    if (name === "email") {
      setFormData((prev) => ({
        ...prev,
        emails: prev.emails?.length
          ? prev.emails.map((email, index) =>
              index === 0
                ? {
                    ...email,
                    email: value,
                  }
                : email,
            )
          : [
              {
                email: value,
                isPrimary: true,
              },
            ],
      }));

      return;
    }

    // Timezone handling
    if (name === "timeZone") {
      setFormData((prev) => ({
        ...prev,
        [name]: value ? Number(value) : null,
      }));

      return;
    }

    // DOB handling
    if (name === "dob") {
      // Allow clearing the field
      if (value === "") {
        setFormData((prev) => ({
          ...prev,
          dob: "",
        }));
        return;
      }

      // Ignore invalid dates
      if (!isValidDate(value)) {
        return;
      }

      setFormData((prev) => ({
        ...prev,
        dob: value,
      }));
      return;
    }

    // Normal fields
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  return {
    formData,
    handleChange,
    setFormData,
  };
};
